"use client"

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { toolsService } from '@edemics/services/toolsService'
import { useAuth } from './auth-provider'
import { DEFAULT_ROLE_MODULES } from '@edemics/lib/system-modules'

interface ModuleContextType {
  /**
   * Role-scoped enabled modules (from tools/getMyModules or the role
   * defaults). Legacy shape; kept in the context type for consumers
   * that already read it.
   */
  enabledModules: string[] | null
  /**
   * Tenant-scoped enabled modules delivered by the BE on /auth/me
   * and login. Every module the tenant has access to given super-admin
   * gates + catalog defaults. `null` means either "not a tenant user"
   * (platform staff bypass) or "BE didn't provide the list" (pre-gate
   * builds or resolution error) — both treated as "no gate".
   */
  tenantEnabledModules: string[] | null
  /**
   * Tenant-scoped explicit-disabled set (2026-08-27) — every catalog
   * key the tenant currently has EXPLICITLY off. This is what
   * isModuleEnabled gates on: a submodule missing from
   * tenantEnabledModules but ALSO missing here is treated as "unknown /
   * enabled by default" — the seed catches up, sidebar keeps rendering.
   * Only explicit-off truly hides items.
   */
  tenantDisabledModules: string[] | null
  /**
   * Tenant-scoped enabled doctypes (2026-08-27, from /auth/me
   * `enabledDoctypes`). Doctype tier is finer-grained than module —
   * hidden doctypes still get referenced by other modules internally
   * per the "everything is connected" invariant; the FE only uses
   * this to hide user-facing surface (list views, forms, menu entries).
   * Same null semantics as tenantEnabledModules.
   */
  tenantEnabledDoctypes: string[] | null
  /**
   * H4 (2026-09-03) — every module key the tenant sees as premium-
   * locked. Only ever a SUBSET of tenantDisabledModules; a key in
   * this list is disabled AND premium-driven, so consumers should
   * render an upsell CTA instead of a bare "not available" state.
   * Null semantics match tenantDisabledModules.
   */
  tenantPremiumModules: string[] | null
  loading: boolean
  isModuleEnabled: (moduleKey: string) => boolean
  isDoctypeEnabled: (doctypeKey: string) => boolean
  /**
   * H4 — is the reason `isModuleEnabled(key)` returns false a premium-
   * gate lock? Callers use this to pick the upsell page vs the
   * generic ModuleUnavailablePage. Returns false for platform users,
   * unknown keys, and non-premium disabled keys.
   */
  isModulePremiumLocked: (moduleKey: string) => boolean
  refreshModules: () => void
}

const ModuleContext = createContext<ModuleContextType>({
  enabledModules: null,
  tenantEnabledModules: null,
  tenantDisabledModules: null,
  tenantEnabledDoctypes: null,
  tenantPremiumModules: null,
  loading: false,
  isModuleEnabled: () => true,
  isDoctypeEnabled: () => true,
  isModulePremiumLocked: () => false,
  refreshModules: () => {},
})

export function ModuleProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const [enabledModules, setEnabledModules] = useState<string[] | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchModules = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setEnabledModules(null)
      return
    }

    // Admin always sees everything (role-scoped filter). The tenant
    // visibility filter is still applied below.
    if (user.role === 'admin') {
      setEnabledModules(null)
      return
    }

    try {
      setLoading(true)
      const modules = await toolsService.getMyModules()
      // Empty array = no assignments configured → use defaults
      if (modules.length === 0) {
        setEnabledModules(null)
      } else {
        setEnabledModules(modules)
      }
    } catch {
      // On error, fall back to defaults
      setEnabledModules(null)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    fetchModules()
  }, [fetchModules])

  // Tenant-visibility set: comes from the auth payload the BE ships
  // on login and /auth/me. Every tenant module the super-admin has
  // enabled for this tenant. `undefined` from the auth provider means
  // pre-gate builds or platform users — treat as "no gate".
  const tenantEnabledModules = user?.enabledModules ?? null
  // Explicit-off set (2026-08-27). See ModuleContextType doc. This is
  // the authoritative source for "should this be hidden?"; the enabled
  // list is kept around for display purposes but no longer participates
  // in the sidebar gate.
  const tenantDisabledModules = user?.disabledModules ?? null

  // Doctype-visibility set (2026-08-27) — same source and null
  // semantics as tenantEnabledModules but at the doctype tier.
  const tenantEnabledDoctypes = user?.enabledDoctypes ?? null

  // H4 (2026-09-03) — premium-locked keys. Same source + null
  // semantics as the other tenant-scoped sets. Undefined on the
  // user object (pre-H4 BE, platform users) → null → "no info,
  // don't gate as premium".
  const tenantPremiumModules = user?.premiumModules ?? null

  const isModulePremiumLocked = useCallback(
    (moduleKey: string): boolean => {
      // Missing list → don't render as premium-locked. Platform users
      // and pre-H4 BE responses both land here.
      if (!tenantPremiumModules) return false
      return tenantPremiumModules.includes(moduleKey)
    },
    [tenantPremiumModules],
  )

  const isDoctypeEnabled = useCallback(
    (doctypeKey: string): boolean => {
      // Session-restore window / no list from BE — treat as allowed
      // so pages don't flash "not available" during the auth flow.
      if (tenantEnabledDoctypes === null) return true
      return tenantEnabledDoctypes.includes(doctypeKey)
    },
    [tenantEnabledDoctypes],
  )

  const isModuleEnabled = useCallback(
    (moduleKey: string): boolean => {
      // ---- Layer 1: tenant-visibility gate (super-admin controlled) ----
      // Only EXPLICIT-off wins here. A key merely missing from
      // enabledModules is treated as "unknown / seed hasn't caught up"
      // and falls through — the FE keeps rendering the submodule even
      // if the BE hasn't listed it. This is the fix for missing sidebar
      // submenu items: previously any submodule key the BE didn't
      // enumerate got hidden, so a stale catalog seed silently pruned
      // the sidebar. Now it takes a real override to hide something.
      if (tenantDisabledModules && tenantDisabledModules.includes(moduleKey)) {
        return false
      }

      // ---- Layer 2: role-scoped assignment (existing behavior) ----
      // Admin sees everything else.
      if (user?.role === 'admin') return true

      // No custom assignments → use hardcoded defaults
      if (enabledModules === null) {
        const role = user?.role || sessionStorage.getItem('userRole') || 'admin'
        const defaults = DEFAULT_ROLE_MODULES[role]
        return defaults ? defaults.includes(moduleKey) : true
      }

      return enabledModules.includes(moduleKey)
    },
    [enabledModules, tenantDisabledModules, user],
  )

  return (
    <ModuleContext.Provider
      value={{
        enabledModules,
        tenantEnabledModules,
        tenantDisabledModules,
        tenantEnabledDoctypes,
        tenantPremiumModules,
        loading,
        isModuleEnabled,
        isDoctypeEnabled,
        isModulePremiumLocked,
        refreshModules: fetchModules,
      }}
    >
      {children}
    </ModuleContext.Provider>
  )
}

export function useModuleContext() {
  return useContext(ModuleContext)
}

export { ModuleContext }
